<template>
    <div class="course__list_table">
        <component-table
            :labels="tableLabels"
            :items="items"
            :component-table-item="CourseListTableItem"
            :sort-queryable="sortQueryable"
            @emit-sort="handleSort"
            @emit-copy-course="openCopyCourseNotify"
            @emit-open-enrol-learners-pop-table="openEnrolLearnersPopTable"
            @emit-open-withdraw-learners-pop-table="openWithdrawLearnersPopTable"
        />
        <pop-table
            ref="enrollearnerspoptable"
            :size="'full'"
            :class="'course__list_table_enrollearnerspoptable'"
            :content-text="''"
            :cancel-button-text="'Close'"
            :eventbus-emit-name="'course-enrolment'"
            :filter-search-placeholder="'Search learners'"
            :filter-search-queryable="true"
            :filter-select-active="true"
            :filter-select-items="enrolFiltersSelect"
            :get-pagination-getter="'getUsersPagination'"
            :get-items-getter="'getUsersToEnrolMappedIdNameStatus'"
            :get-items-getter-mapped="'getUsersToEnrolMappedLabelValue'"
            :get-items-dispatch="'getUsersToEnrol'"
            :get-items-dispatch-data="{ catalogItemId: selectedCatalogItem }"
            :get-items-dispatch-params="listExtraFilters"
            :get-items-dispatch-trigger-name="'trigger-get-users'"
            :item-name="['learner', 'learners']"
            :prevent-close="true"
            :submit-button-animate-loading="true"
            :submit-button-animate-loading-name="'course-bulk-enrolment'"
            :submit-button-text="'Enrol learners'"
            :footer-details-active="true"
            :footer-details-limit-active="true"
            :footer-details-available-text="'Number of available licences'"
            :footer-details-available-value="selectedAvailableLicences"
            :footer-details-selected-text="'Number of users selected'"
            :title-text="'Enrol learners'"
            :labels="[
                {
                    key: 'firstName',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'emailAddress',
                    title: 'Email',
                    type: 'string',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'organisationUnitName',
                    title: 'Org unit',
                    type: 'string',
                    sortable: false
                },
                {
                    key: 'status',
                    title: 'Enrolment status',
                    type: 'status',
                    sortable: false
                },
                {
                    key: 'completionsCount',
                    title: 'No. of completions',
                    type: 'number',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'lastCompletion',
                    title: 'Date of last completion',
                    type: 'datetime',
                    sortable: true,
                    queryable: true
                }
            ]"
            :table-checkbox="true"
            :table-checkbox-name="'course-enrolment'"
            @emit-selected="setPopTableSelectedToEnrol"
            @emit-submit="performEnrolLearners"
            @emit-request-finally="stopOrgUnitsApplyLoading"
        ></pop-table>
        <pop-table
            ref="withdrawlearnerspoptable"
            :size="'full'"
            :class="'course__list_table_withdrawenrollearnerspoptable'"
            :content-text="''"
            :cancel-button-text="'Close'"
            :eventbus-emit-name="'course-withdraw-enrolment'"
            :filter-search-placeholder="'Search learners'"
            :filter-search-queryable="true"
            :filter-select-active="true"
            :filter-select-items="withdrawFiltersSelect"
            :get-pagination-getter="'getUsersPagination'"
            :get-items-getter="'getUsersToWithdrawMappedIdNameStatus'"
            :get-items-getter-mapped="'getUsersToWithdrawMappedLabelValue'"
            :get-items-dispatch="'getUsersToWithdraw'"
            :get-items-dispatch-data="{ catalogItemId: selectedCatalogItem }"
            :get-items-dispatch-trigger-name="'trigger-get-users'"
            :item-name="['learner', 'learners']"
            :prevent-close="true"
            :submit-button-animate-loading="true"
            :submit-button-animate-loading-name="'course-bulk-withdraw-enrolment'"
            :submit-button-text="'Withdraw learners'"
            :title-text="'Withdraw learners'"
            :labels="[
                {
                    key: 'firstName',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'emailAddress',
                    title: 'Email',
                    type: 'string',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'enrolmentState',
                    title: 'Enrolment status',
                    type: 'status',
                    sortable: false
                },
                {
                    key: 'enrolmentDate',
                    title: 'Enrolment date',
                    type: 'datetime',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'lastCompletion',
                    title: 'Last completion',
                    type: 'datetime',
                    sortable: true,
                    queryable: true
                }
            ]"
            :table-checkbox="true"
            :table-checkbox-name="'course-enrolment'"
            @emit-selected="setPopTableSelectedToWithdraw"
            @emit-submit="performWithdrawLearners"
            @emit-request-finally="stopOrgUnitsApplyLoading"
        >
            <template v-slot:footer>
                <div class="columns is-variable is-2 is-vcentered">
                    <div class="column is-narrow">
                        <component-note>
                            <component-title :size="'label'">Users selected</component-title>
                            Enrolled: <strong>{{ countSelectedWithdrawByStatus('enrolled') }}</strong> <br>
                            In progress: <strong>{{ countSelectedWithdrawByStatus('in_progress') }}</strong> <br>
                            Awaiting feedback: <strong>{{ countSelectedWithdrawByStatus('awaiting_feedback') }}</strong>
                        </component-note>
                    </div>
                    <div class="column">
                        <component-button
                            :name="'course-list-table-withdraw-hint'"
                            :theme="''"
                            :icon="true"
                            :icon-raw="true"
                            :icon-name="'help-circle'"
                            :tooltip-active="true"
                            :tooltip-text="`Withdrawing enrolments will immediately remove access to the course.\n\nFor learners who have not started the course, licences will be reclaimed.\n\nLearners who have started the course have consumed a licence and will lose their progress.`"
                            :tooltip-position="'top'"
                        ></component-button>
                    </div>
                </div>
            </template>
        </pop-table>
        <pop-notify
            :class="'pop__notify--copy'"
            ref="notifycopycourse"
            :title-text="'Copy course'"
            :content-text="'Copy course? Newly created clone gets draft status and the same name that its source but with (copy) added to it.'"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Copy'"
            :submit-button-animate-loading="true"
            :prevent-close="true"
            @emit-submit="performCopyCourse"
        />
        <a class="course__list_table_hiddenlink hidden" id="download-course"></a>
    </div>
</template>

<script>
import CourseListTableItem from '@/components/ebs/courses/list/CourseListTableItem'
import ComponentTable from '@/components/default/shared/ComponentTable'
import { User } from '@/User'
import PopTable from '@/components/default/shared/PopTable'
import ComponentNote from '@/components/default/shared/ComponentNote'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentButton from '@/components/default/shared/ComponentButton'
import PopNotify from '@/components/default/shared/PopNotify'

export default {
  components: {
    PopNotify,
    ComponentButton,
    ComponentTitle,
    ComponentNote,
    PopTable,
    ComponentTable
  },
  name: 'course-list-table',
  data () {
    return {
      user: new User(),
      popTableSelectedToEnrol: [],
      popTableSelectedToWithdraw: [],
      selectedCatalogItem: '',
      selectedAvailableLicences: '',
      listExtraFilters: '',
      CourseListTableItem
    }
  },
  props: {
    items: {
      type: [Array, Object]
    },
    sortQueryable: {
      type: Boolean
    }
  },
  computed: {
    routeQueryView () {
      return this.$route.query.view
    },
    tableLabels () {
      let labels = []
      if (this.routeQueryView === 'course-creator') {
        labels = [
          {
            key: 'name',
            title: 'Course',
            type: 'string',
            sortable: true,
            queryable: true
          },
          {
            key: 'duration',
            title: 'Duration',
            type: 'string',
            sortable: false,
            queryable: false
          },
          {
            key: 'labels',
            title: 'Labels',
            type: 'array',
            sortable: false,
            queryable: false
          },
          {
            key: 'type',
            title: 'Type',
            type: 'string',
            sortable: false,
            queryable: false
          },
          {
            key: 'draft',
            title: 'Status',
            type: 'string',
            sortable: true,
            queryable: true
          },
          {
            key: 'actions',
            title: '',
            type: 'string',
            sortable: false
          }
        ]
      } else if (this.routeQueryView === 'admin') {
        labels = [
          {
            key: 'name',
            title: 'Course',
            type: 'string',
            sortable: true,
            queryable: true
          },
          {
            key: 'author',
            title: 'Author',
            type: 'string',
            sortable: false,
            queryable: false
          },
          {
            key: 'duration',
            title: 'Duration',
            type: 'string',
            sortable: false,
            queryable: false
          },
          {
            key: 'labels',
            title: 'Labels',
            type: 'array',
            sortable: false,
            queryable: false
          },
          {
            key: 'type',
            title: 'Type',
            type: 'string',
            sortable: false,
            queryable: false
          },
          {
            key: 'actions',
            title: '',
            type: 'string',
            sortable: false
          }
        ]
      } else {
        labels = [
          {
            key: 'name',
            title: 'Course',
            type: 'string',
            sortable: true,
            queryable: true
          },
          {
            key: 'duration',
            title: 'Duration',
            type: 'string',
            sortable: false,
            queryable: false
          },
          {
            key: 'type',
            title: 'Type',
            type: 'string',
            sortable: false,
            queryable: false
          },
          {
            key: 'enrolmentStatus',
            title: 'Enrolment status',
            type: 'string',
            sortable: false,
            queryable: false
          },
          {
            key: 'actions',
            title: '',
            type: 'string',
            sortable: false
          }
        ]
      }
      return labels
    },
    enrolFiltersSelect () {
      return [
        {
          key: 'workspace_group',
          label: 'Group',
          type: 'multiselect',
          getItemsGetter: 'getLmsSuperadminGroupsMappedIdName',
          getItemsGetterMapped: 'getLmsSuperadminGroupsMappedLabelValue',
          getItemsDispatch: 'lmsSuperadminGroupsGet',
          itemName: ['group', 'groups'],
          searchQueryable: true,
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            }
          ]
        },
        {
          key: 'organisationUnitId',
          label: 'Organisation unit',
          type: 'org-units'
        }
      ]
    },
    withdrawFiltersSelect () {
      return [
        {
          key: 'workspace_group',
          label: 'Group',
          type: 'multiselect',
          getItemsGetter: 'getLmsSuperadminGroupsMappedIdName',
          getItemsGetterMapped: 'getLmsSuperadminGroupsMappedLabelValue',
          getItemsDispatch: 'lmsSuperadminGroupsGet',
          itemName: ['group', 'groups'],
          searchQueryable: true,
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            }
          ]
        },
        {
          key: 'organisationUnitId',
          label: 'Organisation unit',
          type: 'org-units'
        },
        {
          key: 'enrolmentState',
          label: 'Status',
          type: 'checkbox',
          preselected: ['enrolled', 'in_progress', 'awaiting_feedback'],
          values: [
            {
              label: 'Enrolled',
              value: 'enrolled'
            },
            {
              label: 'In progress',
              value: 'in_progress'
            },
            {
              label: 'Awaiting feedback',
              value: 'awaiting_feedback'
            },
            {
              label: 'Completed',
              value: 'completed'
            }
          ]
        }
      ]
    }
  },
  methods: {
    openCopyCourseNotify (data) {
      this.$refs.notifycopycourse.setStoreValue(data)
      this.$refs.notifycopycourse.openNotify()
    },
    closeCopyCourseNotify () {
      this.$refs.notifycopycourse.closeNotify()
    },
    handleListExtraFilters (data) {
      let queryArr = []
      if (data) {
        if (data.orgUnits && data.orgUnits.length) {
          queryArr.push(`filters[organisationUnitId]=${data.orgUnits.join(',')}`)
        }
        if (data.groups && data.groups.length) {
          queryArr.push(`filters[workspace_group]=${data.groups.join(',')}`)
        }
      }
      this.listExtraFilters = queryArr.join('&')

      setTimeout(() => {
        this.$refs.enrollearnerspoptable.$refs.componentlist.getItems()
      }, 10)
    },
    stopOrgUnitsApplyLoading () {
      this.$eventBus.$emit('stop-org-units-filter-apply')
    },
    openEnrolLearnersPopTable (data) {
      this.listExtraFilters = ''
      this.selectedCatalogItem = data.catalogId
      this.selectedAvailableLicences = data.availableLicences
      this.$refs.enrollearnerspoptable.openPop()
    },
    openWithdrawLearnersPopTable (data) {
      this.listExtraFilters = ''
      this.selectedCatalogItem = data.catalogId
      this.$refs.withdrawlearnerspoptable.openPop()
    },
    handleSort (data) {
      this.$emit('emit-sort', data)
    },
    countSelectedWithdrawByStatus (value) {
      const selected = this.popTableSelectedToWithdraw
      return selected.length ? selected.filter(obj => obj.enrolmentState === value).length : 0
    },
    setPopTableSelectedToEnrol (data) {
      this.popTableSelectedToEnrol = data
    },
    setPopTableSelectedToWithdraw (data) {
      this.popTableSelectedToWithdraw = data
    },
    performCopyCourse () {
      const courseId = this.$refs.notifycopycourse.storeValue

      this.$store.dispatch('ebsCourseClone', courseId).finally(() => {
        this.closeCopyCourseNotify()
      }).then(() => {
        this.$emit('emit-perform-get-items')
      })
    },
    performEnrolLearners () {
      this.$store.dispatch('ebsEnrolmentBulkEnrol', { catalogItemId: this.selectedCatalogItem, learners: this.popTableSelectedToEnrol }).then(response => {
        if (this.$refs.enrollearnerspoptable) {
          this.$refs.enrollearnerspoptable.closePop()
          this.$emit('emit-perform-get-items')
        }
      }).finally(() => {
        this.$eventBus.$emit('stop-course-bulk-enrolment')
        this.setPopTableSelectedToEnrol([])
      })
    },
    performWithdrawLearners () {
      this.$store.dispatch('ebsEnrolmentBulkWithdraw', { catalogItemId: this.selectedCatalogItem, learners: this.popTableSelectedToWithdraw }).then(response => {
        if (this.$refs.withdrawlearnerspoptable) {
          this.$refs.withdrawlearnerspoptable.closePop()
          this.$emit('emit-perform-get-items')
        }
      }).finally(() => {
        this.$eventBus.$emit('stop-course-bulk-withdraw')
        this.setPopTableSelectedToWithdraw([])
      })
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/courses/courselisttable";
</style>
